import { useCallback, useEffect, useState } from "react";
import { Network, ChevronDown, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { http } from "@/lib/api";

// Lote 127 — cartão "Proxy do DJEN" da Administração: qual proxy está em uso (principal ou reserva), consumo estimado
// do pacote de dados, "Testar agora" e o passo a passo para ligar uma reserva. Nunca mostra endereço, usuário ou senha.
const fmt = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
};
export const tamanho = (b) => {
  if (b == null) return "—";
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(2).replace(".", ",")} GB`;
  if (b >= 1048576) return `${(b / 1048576).toFixed(b >= 10485760 ? 0 : 1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
};
const NIVEL = { erro: "border-red-200 bg-red-50 text-red-900", aviso: "border-amber-200 bg-amber-50 text-amber-900" };

export default function ProxyDjenCard() {
  const [st, setSt] = useState(null);
  const [saldo, setSaldo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [teste, setTeste] = useState(null);
  const [ajuda, setAjuda] = useState(false);
  const load = useCallback(() => http.get("/admin/djen-proxy/status").then((r) => { setSt(r.data); if (r.data?.ultimo_teste) setTeste((t) => t || r.data.ultimo_teste); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const salvarSaldo = async () => {
    const n = Number(String(saldo).replace(",", "."));
    if (!Number.isFinite(n) || n < 0) { toast.error("Digite o saldo em GB, por exemplo 1,9"); return; }
    setSalvando(true);
    try {
      const r = await http.post("/admin/djen-proxy/saldo", { saldo_gb: n });
      setSt(r.data); setSaldo(""); toast.success("Saldo registrado", { description: "A estimativa passa a descontar o que o app usar a partir de agora." });
    } catch (e) { toast.error(e?.response?.data?.detail || "Não consegui salvar"); }
    finally { setSalvando(false); }
  };
  const testar = async () => {
    setTestando(true);
    try {
      const r = await http.post("/admin/djen-proxy/testar", {}, { timeout: 120000 });
      setTeste(r.data); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "O teste não pôde ser feito"); }
    finally { setTestando(false); }
  };

  if (!st) return null;
  const reserva = st.ativo === "reserva";
  const u = st.uso || {};
  return (
    <section data-testid="admin-proxy-djen" className="bg-white border border-stone-200 rounded-md p-5 mb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-semibold text-stone-900 flex items-center gap-2">
            <Network className="w-4 h-4 text-stone-500" strokeWidth={1.75} /> Proxy do DJEN
            <span data-testid="proxy-em-uso" data-ativo={st.ativo}
              className={`inline-flex items-center gap-1 text-xs font-semibold ${reserva ? "text-amber-700" : "text-emerald-700"}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> {reserva ? "reserva em uso" : "principal em uso"}
            </span>
          </h2>
          <p className="text-sm text-stone-600 mt-1" data-testid="proxy-linha-config">
            Principal: <b className="text-stone-900">{st.configurado?.principal ? "configurado" : "não configurado"}</b>
            {st.ultimo_ok?.principal ? ` · respondeu em ${fmt(st.ultimo_ok.principal)}` : ""}
            {" · "}Reserva: <b className="text-stone-900">{st.configurado?.reserva ? "configurada" : "não configurada"}</b>
            {st.ultimo_ok?.reserva ? ` · respondeu em ${fmt(st.ultimo_ok.reserva)}` : ""}
          </p>
        </div>
        <Button data-testid="proxy-testar" onClick={testar} disabled={testando || (!st.configurado?.principal && !st.configurado?.reserva)} className="gap-2 bg-stone-100 text-stone-900 border border-stone-200 hover:bg-stone-200 shadow-none">
          {testando ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {testando ? "Testando…" : "Testar agora"}
        </Button>
      </div>

      {(st.alertas || []).map((a) => (
        <div key={a.tipo} role="alert" data-testid={`proxy-alerta-${a.tipo}`} className={`mt-3 flex gap-2.5 rounded-md border px-3.5 py-2.5 text-sm ${NIVEL[a.nivel] || NIVEL.aviso}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
          <p>{a.texto}{a.desde ? ` (desde ${fmt(a.desde)})` : ""}</p>
        </div>
      ))}

      {teste?.resultados?.length > 0 && (
        <ul data-testid="proxy-teste-resultado" className="mt-3 text-sm space-y-1">
          {teste.resultados.map((r) => (
            <li key={r.proxy} data-testid={`proxy-teste-${r.proxy}`} className="flex flex-wrap items-center gap-x-2">
              <span className={`font-semibold ${r.ok ? "text-emerald-700" : "text-red-700"}`}>{r.proxy === "principal" ? "Principal" : "Reserva"}: {r.ok ? "passou" : "falhou"}</span>
              <span className="text-stone-600">
                {r.ok ? `DJEN respondeu em ${((r.ms || 0) / 1000).toFixed(1).replace(".", ",")} s` : r.erro}
                {r.pais ? ` · IP ${r.pais}${r.org ? ` (${r.org})` : ""}` : ""}
              </span>
            </li>
          ))}
          <li className="text-xs text-stone-400">Teste de {fmt(teste.quando)} · não troca o proxy em uso</li>
        </ul>
      )}

      <div className="mt-4 border-t border-stone-200 pt-3" data-testid="proxy-consumo">
        <p className="text-sm font-semibold text-stone-700">Pacote de dados do principal <span className="font-normal text-stone-500">(estimativa do app)</span></p>
        {u.saldo_base_bytes ? (
          <div className="mt-1.5">
            <div className="h-2 rounded-full bg-stone-100 overflow-hidden" role="progressbar" aria-valuenow={u.pct} aria-valuemin={0} aria-valuemax={100}>
              <div className={`h-full ${u.pct >= 100 ? "bg-red-500" : u.pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.max(2, u.pct || 0)}%` }} />
            </div>
            <p className="text-sm text-stone-600 mt-1.5" data-testid="proxy-consumo-texto">
              Usado desde {fmt(u.saldo_base_em)?.slice(0, 5)}: <b className="text-stone-900">{tamanho(u.principal_bytes)}</b> · restam cerca de <b className="text-stone-900">{tamanho(u.restante_bytes)}</b>
              {u.ritmo_mb_mes != null ? ` · ritmo de ${String(u.ritmo_mb_mes).replace(".", ",")} MB por mês` : ""}
              {u.dias_restantes != null ? ` · dura cerca de ${u.dias_restantes} dias` : ""}
            </p>
          </div>
        ) : (
          <p className="text-sm text-stone-600 mt-1">Informe o saldo que o painel do fornecedor mostra hoje e o app passa a acompanhar o consumo e avisar antes de acabar.</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input data-testid="proxy-saldo-input" inputMode="decimal" value={saldo} onChange={(e) => setSaldo(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") salvarSaldo(); }}
            placeholder="Saldo no painel, em GB" aria-label="Saldo atual em GB" className="w-56 h-10 bg-white" />
          <Button data-testid="proxy-saldo-salvar" onClick={salvarSaldo} disabled={salvando || !saldo.trim()} className="bg-stone-100 text-stone-900 border border-stone-200 hover:bg-stone-200 shadow-none">Registrar saldo</Button>
        </div>
      </div>

      <button type="button" data-testid="proxy-ajuda-toggle" onClick={() => setAjuda((v) => !v)} aria-expanded={ajuda}
        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-stone-600 hover:text-stone-900">
        Como funciona e como ligar uma reserva <ChevronDown className={`w-3.5 h-3.5 transition-transform ${ajuda ? "rotate-180" : ""}`} />
      </button>
      {ajuda && (
        <div data-testid="proxy-ajuda" className="mt-2 text-sm text-stone-700 space-y-2 max-w-3xl">
          <p>O DJEN só atende IPs brasileiros reconhecidos; por isso as buscas saem por um proxy pago. Se o <b>principal</b> falhar (bloqueio, plano vencido, saldo acabado ou sem resposta) e existir uma <b>reserva</b>, o app repete a busca pela reserva sozinho, avisa por e-mail e faixa no Painel, e a cada 6 horas testa o principal para voltar a ele.</p>
          <p>O consumo é uma estimativa (soma do que o app baixa pelo proxy); o saldo oficial é o do painel do fornecedor. Recarregue sempre por compra avulsa, não por assinatura.</p>
          <p>Para ligar uma reserva: contrate um IP brasileiro de outro fornecedor, teste com o script <b>testar-proxy-djen.sh</b> e, só se passar, no Terminal do Mac rode (trocando pelos dados do fornecedor; a senha só existe aí):</p>
          <p><code className="text-xs bg-stone-100 px-1.5 py-1 rounded break-all">fly secrets set DJEN_PROXY_URL_RESERVA="http://USUARIO:SENHA@servidor:porta" -a jus-agenda-app</code></p>
          <p>O app reinicia sozinho; depois use <b>Testar agora</b> aqui para conferir os dois.</p>
        </div>
      )}
    </section>
  );
}
